// @ts-strict-ignore
import { Renderer } from './renderer';
import { AmbientLight, DirectionalLight, Object3D, OrthographicCamera, Scene, Vector3, WebGLCapabilities } from 'three';
import { ContainerService } from '../model3d/container.service';
import { Object3DUtils } from '../common/util/object-3d-utils';
import { Injectable, OnDestroy } from '@angular/core';
import { ArticleContainer } from '../model3d/article-container';
import { ThreeDMouseControl } from '../common/scene/controls/3d-mouse-control';
import { HttpClient } from '@angular/common/http';
import { CameraControl } from '../common/scene/controls/camera-control';
import { GlobalArticleContainerUtils } from '../model3d/util/global-article-container-utils';
import { ProjectModel } from '@project/model/project.model';
import { filter, map } from 'rxjs/operators';
import { Font, FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { ContainerUtils } from '../model3d/container.utils';

@Injectable({ providedIn: 'root' })
export class ConfiguratorScene implements OnDestroy {
  private _canvas?: HTMLCanvasElement;

  private _renderer: Renderer;
  private _scene: Scene;
  private _camera?: OrthographicCamera;
  private _controls?: CameraControl;

  private _isHiddenSceneObjects: boolean = false;
  private _isHiddenObjectsEnabled: boolean = true;

  private _defaultFont?: Font;

  constructor(private componentUtils: ContainerService,
              private httpClient: HttpClient,
              private projectModel: ProjectModel,
              private globalArticleContainerUtils: GlobalArticleContainerUtils,
  ) {
    this._renderer = new Renderer();
    this._scene = new Scene();
    this.loadFont();
  }

  public get scene(): Scene {
    return this._scene;
  }

  public get rendererCapabilities(): WebGLCapabilities {
    return this._renderer.capabilities;
  }

  public get camera(): OrthographicCamera {
    return this._camera;
  }

  public get controls(): CameraControl {
    return this._controls;
  }

  public get renderer(): Renderer {
    return this._renderer;
  }

  public get defaultFont(): Font {
    return this._defaultFont;
  }

  public get canvas(): HTMLCanvasElement {
    return this._canvas;
  }

  public get canvasWidth(): number {
    return this._canvas?.clientWidth;
  }

  public set isHiddenObjectsEnabled(value: boolean) {
    this._isHiddenObjectsEnabled = value;
  }

  public ngOnDestroy(): void {
    this.controls?.dispose();
  }

  public create(canvas: HTMLCanvasElement): void {
    this._canvas = canvas;
    this.initScene(canvas);
  }

  public resize(delta: number): void {
    if (this._canvas){
      this._renderer.setSize(this._canvas.width, this._canvas.height);

      if (this.camera) {
        this.camera.left = (-this._canvas.clientWidth / 2) * delta;
        this.camera.right = (this._canvas.clientWidth / 2) * delta;
        this.camera.top = (this._canvas.clientHeight / 2) * delta;
        this.camera.bottom = (-this._canvas.clientHeight / 2) * delta;
        this.camera.updateProjectionMatrix();
      }
    }

    this.updateWirecontourVisibility();
    this.renderScene();
  }

  public updateWirecontourVisibility(): void {
    this.controlsChanged();
  }

  public renderScene(): void {
    this._renderer?.invalidate3DView();
  }

  public addObjectToScene(object: Object3D) {
    this.scene.add(object);
  }

  private initScene(canvas: HTMLCanvasElement): void {
    this.clearScene();

    this.createLights();

    this._camera = this.createCamera(canvas);

    this._scene.add(this._camera);
    this._camera.lookAt(this._scene.position);

    this.initRenderer(canvas, this._camera);
    const threeDMouseControl = new ThreeDMouseControl(this.httpClient, this._camera, () => this.renderScene());
    this._controls = new CameraControl(
      threeDMouseControl,
      canvas,
      this._camera,
      () => this.renderScene(),
      () => this.updateWirecontourVisibility(),
    );

    this.projectModel.projectChanged$.pipe(
      filter(change => change.type === 'ProjectRefreshed'),
      map(() => this.globalArticleContainerUtils.calculateBBoxForAllArticlesInGlobal()),
      filter(box3 => box3 && !box3.isEmpty()),
    ).subscribe(box3 => threeDMouseControl.setModelExtents(box3));
  }

  private clearScene(): void {
    if (!this.scene) {
      return;
    }
    let i: number = this.scene.children.length;
    while (i--) {
      const object3D: Object3D = this.scene.children[i];
      if (ContainerUtils.isGlobalContainer(object3D)) {
        this.scene.children.splice(i, 1);
        continue;
      }
      Object3DUtils.destroy(object3D);
    }
    Object3DUtils.destroy(this.scene);
  }

  private createLights(): void {
    const ambientLight = new AmbientLight(0xffffff, 0.7 * Math.PI);
    this._scene.add(ambientLight);

    const backLight = this.createDirectionalLight(new Vector3(1000, 2000, 0), 0xffffff, 0.37);
    this._scene.add(backLight);

    const frontLight = this.createDirectionalLight(new Vector3(-1000, -2000, 0), 0xffffff, 0.37);
    this._scene.add(frontLight);
  }

  private createDirectionalLight(position: Vector3, color: number, intensity: number = 1) {
    const directionalLight = new DirectionalLight(color, intensity * Math.PI);
    directionalLight.position.set(position.x, position.y, position.z);
    directionalLight.position.normalize();
    return directionalLight;
  }

  private createCamera(canvas: HTMLCanvasElement): OrthographicCamera {
    const camera = new OrthographicCamera(
      canvas.clientWidth / -2,
      canvas.clientWidth / 2,
      canvas.clientHeight / 2,
      canvas.clientHeight / -2,
      -50000,50000
    );
    // not all required elements are on layer 0 (e.g. layermanager-elements) so we enable all layers here
    camera.layers.enableAll();
    return camera;
  }

  private initRenderer(canvas: HTMLCanvasElement, camera: OrthographicCamera): void {
    this._renderer.init(canvas, this._scene, camera);
  }

  private controlsChanged(): void {
    if (!this._controls || this._isHiddenSceneObjects === this._controls.hideSceneObjects || !this._isHiddenObjectsEnabled) {
      return;
    }

    const articleContainers: ArticleContainer[] = this._controls.hiddenSceneObjects && this._controls.hiddenSceneObjects.length > 0
      ? this._controls.hiddenSceneObjects
      : this.componentUtils.enumerateArticleContainersInGlobal();

    if (!articleContainers || articleContainers.length === 0) {
      return;
    }

    if (this._controls.hideSceneObjects) {
      this._isHiddenSceneObjects = true;
      articleContainers.forEach((articleContainer: ArticleContainer) => {
        articleContainer.hide();
      });
    } else {
      this._isHiddenSceneObjects = false;
      articleContainers.forEach((articleContainer: ArticleContainer) => {
        articleContainer.show();
      });
    }
  }

  public setFocusAtCanvas(): void {
    this._canvas?.focus();
  }

  private loadFont(): void {
    if (!this._defaultFont) {
      const loader = new FontLoader();
      loader.load('ng-assets/fonts/helvetiker_regular.typeface.js', (font) => (this._defaultFont = font));
    }
  }
}
